import React from 'react';
import { formatCurrency, formatPercent, formatMoney, signClass } from '../../hooks/useResultsAnalysis';

const getQualityColor = (quality) => {
  switch (quality) {
    case 'Excellent': return 'bg-surface-elevated text-terminal-green';
    case 'Good': return 'bg-surface-elevated text-terminal-cyan';
    case 'Fair': return 'bg-surface-elevated text-terminal-amber';
    case 'Poor': return 'bg-surface-elevated text-terminal-red';
    default: return 'bg-surface-elevated text-txt-primary';
  }
};

export default function ResultsDashboard({ analysis, recommendedStrategy, losing, strategyLabels }) {
  const { sameHorizon } = analysis;
  const horizonOptions = [
    { label: 'Fix & Flip, reinvested', data: sameHorizon.flip, show: true },
    { label: 'Long-Term Rental', data: sameHorizon.ltr, show: analysis.monthlyRent > 0 },
    { label: 'Short-Term Rental', data: sameHorizon.str, show: analysis.annualStrRevenue > 0 && !analysis.nightlyRateEstimated },
  ].filter((option) => option.show);
  const most = horizonOptions.reduce((best, option) => (option.data.gain > best.data.gain ? option : best));
  const breakEven = analysis.monthlyRent > 0 ? sameHorizon.ltr.breakEvenAppreciation : null;

  return (
    <>
      {/* Dashboard Header */}
      <div className="bg-surface-elevated rounded p-6 mb-6 border border-terminal-green">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-terminal-green">
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-1">Property</h3>
            <p className="text-2xl sm:text-3xl font-bold">${formatCurrency(analysis.purchasePrice)}</p>
            <p className="text-sm opacity-80">Purchase Price</p>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-1">Top-Scoring Strategy</h3>
            <p className="text-2xl font-bold">{strategyLabels[recommendedStrategy]}</p>
            <p className="text-sm opacity-80">
              {losing === 'all' ? 'Every strategy loses money. This one loses least' : losing === 'top' ? 'Scores highest on your priorities, but loses money' : 'Based on your priorities'}
            </p>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-1">Most After {sameHorizon.years} Years</h3>
            <p className={`text-2xl sm:text-3xl font-bold ${signClass(most.data.gain)}`}>{most.data.gain >= 0 ? '+' : ''}{formatMoney(most.data.gain)}</p>
            <p className="text-sm opacity-80">{most.label}, from the same starting cash</p>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-1">Holding Beats Flipping</h3>
            <p className="text-2xl sm:text-3xl font-bold">
              {breakEven == null ? 'n/a' : breakEven <= 0 ? 'Without growth' : `Above ${formatPercent(breakEven)}%`}
            </p>
            <p className="text-sm opacity-80">
              {breakEven == null ? 'No rent entered' : breakEven <= 0 ? 'Even if prices stay flat' : 'Yearly price growth needed'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Purchase & Renovation */}
        <div className="bg-surface-primary rounded p-4">
          <h4 className="text-lg font-semibold text-txt-primary mb-3">Purchase & Renovation</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-txt-muted">Purchase Price</span>
              <span className="font-medium">${formatCurrency(analysis.purchasePrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Down Payment</span>
              <span className="font-medium">${formatCurrency(analysis.downPayment)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Renovation Cost</span>
              <span className="font-medium">${formatCurrency(analysis.renovationCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Loan Amount</span>
              <span className="font-medium">${formatCurrency(analysis.loanAmount)}</span>
            </div>
          </div>
        </div>

        {/* ARV & Projected Value */}
        <div className="bg-surface-primary rounded p-4">
          <h4 className="text-lg font-semibold text-txt-primary mb-3">ARV & Projected Value</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-txt-muted">Expected Selling Price</span>
              <span className="font-medium">${formatCurrency(analysis.expectedSellingPrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">ARV-to-Purchase</span>
              <span className="font-medium">{formatPercent(analysis.arvToPurchaseRatio * 100)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Renovation-to-ARV</span>
              <span className="font-medium">{formatPercent(analysis.renovationToArvRatio)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">70% Rule Max Offer</span>
              <span className={`font-medium ${analysis.purchasePrice > analysis.maxOfferSeventyRule ? 'text-terminal-amber' : 'text-terminal-green'}`}>
                ${formatCurrency(analysis.maxOfferSeventyRule)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-txt-muted">Net Profit (Flip)</span>
              <span className={`font-medium ${signClass(analysis.netProfit)}`}>{formatMoney(analysis.netProfit)}</span>
            </div>
          </div>
        </div>

        {/* Deal Assessment */}
        <div className="bg-surface-primary rounded p-4">
          <h4 className="text-lg font-semibold text-txt-primary mb-3">Flip Grade</h4>
          <div className="mb-3">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getQualityColor(analysis.dealQuality)}`}>
              {analysis.dealQuality} as a flip
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-txt-muted">Flip ROI (holding period)</span>
                <span className="font-medium">{formatPercent(analysis.roi)}%</span>
              </div>
              <div className="w-full bg-surface-elevated rounded-full h-2">
                <div
                  className="bg-terminal-cyan h-2 rounded-full"
                  style={{ width: `${Math.min(Math.max(analysis.roi, 0), 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-txt-muted">ARV Ratio</span>
                <span className="font-medium">{formatPercent(analysis.arvToPurchaseRatio * 100)}%</span>
              </div>
              <div className="w-full bg-surface-elevated rounded-full h-2">
                <div
                  className="bg-terminal-cyan h-2 rounded-full"
                  style={{ width: `${Math.min(Math.max((analysis.arvToPurchaseRatio - 1) * 200, 0), 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-txt-muted">Reno-to-ARV</span>
                <span className="font-medium">{formatPercent(analysis.renovationToArvRatio)}%</span>
              </div>
              <div className="w-full bg-surface-elevated rounded-full h-2">
                <div
                  className="bg-terminal-green h-2 rounded-full"
                  style={{ width: `${Math.min(Math.max(100 - analysis.renovationToArvRatio * 2, 0), 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Industry Standards */}
        <div className="bg-surface-primary rounded p-4">
          <h4 className="text-lg font-semibold text-txt-primary mb-3">Flip Rules of Thumb</h4>
          <div className="space-y-2">
            <div className={`p-2 rounded ${getQualityColor('Excellent')}`}>
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Excellent</span>
                <span>ROI &ge; 30%, ARV &ge; 1.3x, Reno &le; 20%</span>
              </div>
            </div>
            <div className={`p-2 rounded ${getQualityColor('Good')}`}>
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Good</span>
                <span>ROI &ge; 20%, ARV &ge; 1.2x, Reno &le; 25%</span>
              </div>
            </div>
            <div className={`p-2 rounded ${getQualityColor('Fair')}`}>
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Fair</span>
                <span>ROI &ge; 10%, ARV &ge; 1.1x, Reno &le; 30%</span>
              </div>
            </div>
            <div className={`p-2 rounded ${getQualityColor('Poor')}`}>
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Poor</span>
                <span>Below Fair thresholds</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
